import React, { useEffect, useRef, useState } from "react";
import { handleKeyUp, handleKeyPress } from "../../ObjectPropertyBox/functions/PropertyBoxFunctions";
import { AnnotationPropType } from "../Annotation";
// import AnnotationTextMenu from "../AnnotationTextMenu/AnnotationTextMenu";
import RTE, { Quill } from "react-quill";
import { Interweave } from "interweave";
import { BASE_PAGES_QUILL_FONT_SIZE_WHITELIST, OLD_EM_FONT_WHITELIST } from "../AnnotationTextMenu/constants";
import { adjustLabelHeight } from "../AnnotationFunctions/AnnotationLabelFunctions";
import { LabelTextEditorMenu } from "../../Annotations/components/LabelTextEditorMenu";
import { ElementTypes } from "../../../pageTypes/BasicPage_Player/components/IBasePage";
import _ from "lodash";
import { updateTextAngleBrackets } from "../../../pageTypes/BasicPage_Player/components/BaseFreeForm/functions/formatConversion";
import { RangeStatic } from "quill";
// import { HighlightAnnotation } from "../../Annotations/HighlightAnnotation";

type LabelType = AnnotationPropType & {
  editableIndex: number;
  isActive: boolean;
  isMenuShown: boolean;
  handleFormatChange(e: any, funct: any, value: any, keyValue: any): void;
  // refFunc: (element: HTMLDivElement | null, index: number) => void,
  setEditableIndex(number: number): void;
  setElementType(value: ElementTypes): void;
  setIsActive(boolean: boolean): void;
  setIsMenuShown(boolean: boolean): void;
  setNodeToUpdate(obj: unknown): void;
  setTarget: (val: unknown) => void;
  updateLabel: (text: string, scrollWidth: number, scrollHeight: number) => void;
  parentCanvasId?: string;
};

const AnnotationLabel = (props: LabelType) => {
  const {
    annotation,
    editableIndex,
    index,
    isActive,
    isMenuShown,
    kp,
    handleFormatChange,
    markSelected,
    setEditableIndex,
    setElementType,
    setIsActive,
    setIsMenuShown,
    setNodeToUpdate,
    setTarget,
    updateLabel,
    parentCanvasId,
  } = props;
  const [selectedFontSize, setSelectedFontSize]: [string, React.Dispatch<React.SetStateAction<string>>] =
    useState<string>("1em");
  const quillRef: React.RefObject<any> = useRef<any>(null);
  const annotationRef = React.useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const formats: Array<string> = [
    "background",
    "bold",
    "color",
    "font",
    "italic",
    "size",
    "underline",
    "indent",
    "align",
  ];
  const placeHolderCSS =
    "color: rgba(0,0,0,0.6); font-style: italic; pointer-events: none; padding-left: calc(.25rem + 2px);";

  const placeholderString = `<span style="${placeHolderCSS}">Enter Text Here...</span>`;

  const isDisplayed = typeof annotation.isDisplayed === "undefined" || annotation.isDisplayed;

  useEffect(() => {
    const sizeStyle = Quill.import("attributors/style/size");
    sizeStyle.whitelist = [...BASE_PAGES_QUILL_FONT_SIZE_WHITELIST, ...OLD_EM_FONT_WHITELIST];
    Quill.register(sizeStyle, true);
  }, []);

  useEffect(() => {
    if (!isMenuShown && annotationRef.current) {
      (annotationRef.current as HTMLDivElement).style.zIndex = `${annotation.zIndex}`;
    }
  }, [isActive]);

  // Compute floating menu position when active/shown or on resize/scroll
  useEffect(() => {
    function updateMenuPosition() {
      if (!annotationRef.current) return;
      const rect = annotationRef.current.getBoundingClientRect();
      let parentElement = null;
      // Check if we're inside a modal by looking for modal-related elements in the DOM tree
      const isInModal = !!parentCanvasId;

      if (parentCanvasId) {
        parentElement = document.getElementById(parentCanvasId as string);
      } else {
        parentElement = annotationRef.current.parentElement;
      }

      // get the player area to make sure the menu is not outside of the
      // blue canvas
      const parentRect = parentElement?.getBoundingClientRect();
      const menuHeight = 38;
      const menuWidth = 483;
      const padding = 10;

      // Account for the CSS default positioning (-2.5rem top, 4rem left)
      const cssTopOffset = -40; // -2.5rem ≈ -40px
      const cssLeftOffset = 64; // 4rem ≈ 64px

      // Compute desired position in viewport coords
      const centerX = rect.left + rect.width / 2 - menuWidth / 2;
      // default to above the label
      let desiredY = rect.top - menuHeight - padding;
      let shouldPositionBelow = false;

      if (parentRect) {
        // Calculate where the menu would actually appear after all transforms
        const finalMenuTop = desiredY + cssTopOffset;
        const canvasTop = parentRect.top;

        // Check if the final menu position would be above the canvas
        if (finalMenuTop < canvasTop) {
          shouldPositionBelow = true;
        }
      }

      // Position below the label if it would go outside bounds
      if (shouldPositionBelow) {
        desiredY = rect.bottom + padding + 30; // Extra padding to avoid blocking the label
      }

      // convert to parent coords if parent exists (toolbar is positioned within parent)
      let x = centerX;
      let y = desiredY;

      if (!isInModal && parentRect) {
        // Only convert to parent coords if we're NOT in a modal
        x -= parentRect.left;
        y -= parentRect.top;
      }

      // Subtract the CSS offsets to counteract them since we're using transform
      x -= cssLeftOffset;
      y -= cssTopOffset;

      // Enhanced horizontal bounds checking
      if (parentRect && !isInModal) {
        // For non-modal context, keep menu inside canvas
        const canvasLeft = 0; // Left edge of canvas in parent coords
        const canvasRight = parentRect.width; // Right edge of canvas in parent coords
        const menuMargin = 100; // Minimum margin from canvas edges

        // Calculate the final menu position after CSS offsets
        const finalMenuLeft = x + cssLeftOffset;
        const finalMenuRight = finalMenuLeft + menuWidth;

        // Check if menu would go outside canvas bounds horizontally
        if (finalMenuLeft < canvasLeft + menuMargin) {
          // Menu would go off the left edge - position it at the left margin
          x = canvasLeft + menuMargin - cssLeftOffset;
        } else if (finalMenuRight > canvasRight - menuMargin) {
          // Menu would go off the right edge - position it at the right margin
          x = canvasRight - menuWidth - menuMargin - cssLeftOffset;
        }
      } else {
        // Fallback for viewport bounds
        const maxWidth = window.innerWidth ?? 0;
        const minX = 10 - cssLeftOffset;
        const maxX = maxWidth - menuWidth - 10 - cssLeftOffset;
        x = Math.max(minX, Math.min(x, maxX));
      }

      setMenuPosition({ x, y });
    }
    if (isMenuShown && isActive) {
      updateMenuPosition();
      window.addEventListener("resize", updateMenuPosition);
      window.addEventListener("scroll", updateMenuPosition, true);
      return () => {
        window.removeEventListener("resize", updateMenuPosition);
        window.removeEventListener("scroll", updateMenuPosition, true);
      };
    }
  }, [isMenuShown, isActive, parentCanvasId]);

  /**
   * This useEffect is to actually focus the label onClick, since after clicking
   * even though the floating editor menu is shown, the user cannot type in the input.
   */
  useEffect(() => {
    if (isMenuShown && isActive && quillRef.current) {
      setTimeout(() => {
        if (quillRef.current?.editor) {
          quillRef.current.editor.focus();
        }
      }, 100);
    }
  }, [isMenuShown, isActive]);

  // Close when clicking outside of the annotation or the toolbar
  useEffect(() => {
    function handleExternalClick(e: MouseEvent) {
      const menuEl = document.getElementById(`menu-label-${index}`);
      if (
        annotationRef.current &&
        !annotationRef.current.contains(e.target as Node) &&
        menuEl &&
        !menuEl.contains(e.target as Node)
      ) {
        setIsMenuShown(false);
        setIsActive(false);
        (annotationRef.current as HTMLDivElement).style.zIndex = `${annotation.zIndex}`;
      }
    }
    if (isMenuShown) {
      document.addEventListener("mousedown", handleExternalClick);
      document.addEventListener("mouseup", handleExternalClick);
      return () => {
        document.removeEventListener("mousedown", handleExternalClick);
        document.removeEventListener("mouseup", handleExternalClick);
      };
    }
  }, [isMenuShown]);

  function handleTextChange(text: string) {
    const scrollWidth = 17;
    let scrollHeight = 28;
    if (text === "<p><br></p>") text = "";
    //Scroll width commented out temporarily due to lack of a solution to grow label with font size change
    // if(quillRef.current?.editingArea?.scrollWidth) {
    //   scrollWidth = adjustLabelWidth(quillRef.current.editingArea.scrollWidth, kp.props.pageDims as DOMRect);
    //   scrollWidth = scrollWidth > annotation.width ? scrollWidth : annotation.width;
    // }

    if (quillRef.current?.editingArea?.scrollHeight >= scrollHeight) {
      scrollHeight = adjustLabelHeight(quillRef.current.editingArea.scrollHeight, kp.props.pageDims as DOMRect);
    }
    updateLabel(text, scrollWidth, scrollHeight);
  }

  const doTextClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    // setIsActive(false);
    setEditableIndex(index);
    setElementType("annotations");
    setNodeToUpdate(annotation);
    setTarget(null);
    setIsMenuShown(true);
    setIsActive(true);
    (e.currentTarget.parentElement as HTMLDivElement).style.outline =
      annotation.borderColor && annotation.borderColor !== "transparent"
        ? `${annotation.strokeWidth}px solid ${annotation.borderColor}`
        : "2px #ADFF2F dashed";
    (e.currentTarget.parentElement as HTMLDivElement).style.zIndex = "501";
  };

  const handleLabel2Click = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsMenuShown(false);
    setIsActive(false);
    markSelected(e.currentTarget.parentElement as HTMLDivElement, index, "annotations");
  };

  function handleHoverIn(e: React.SyntheticEvent<HTMLDivElement>) {
    if (isActive || isMenuShown) {
      return;
    }
    (e.currentTarget.parentElement as HTMLDivElement).style.zIndex = "499";
    // setIsHovering(true);
  }

  function handleHoverOut(e: React.SyntheticEvent<HTMLDivElement>) {
    if (!isActive && !isMenuShown) {
      if (index !== editableIndex) {
        (e.currentTarget.parentElement as HTMLDivElement).style.zIndex = `${annotation.zIndex}`;
      }
    }
  }

  function handleSelectionChange(range: RangeStatic) {
    if (range !== null) {
      const attr = quillRef.current.editor.getFormat(range.index, range.length);
      if (_.has(attr, "size")) {
        setSelectedFontSize(attr.size);
      } else {
        setSelectedFontSize("1em");
      }
    }
  }

  const display = () => {
    const rotate = annotation?.transform?.rotate ? annotation?.transform?.rotate : 0;
    // const scaleX = annotation?.transform?.scaleX;
    // const scaleY = annotation?.transform?.scaleY;
    // const matrix = annotation?.transform?.matrix3d;

    return (
      <React.Fragment>
        {isMenuShown && isActive ? (
          <LabelTextEditorMenu
            id={`menu-label-${index}`}
            isLabel={true}
            formats={formats}
            hide={!isMenuShown || !isActive}
            style={{
              transform: `translate3d(${menuPosition.x}px, ${menuPosition.y}px, 0)`,
              zIndex: 5000,
              position: "fixed",
            }}
          />
        ) : null}
        <div
          // ref={element => refFunc(element, index)}
          id={`label-${index}`}
          ref={annotationRef}
          className="annotation label"
          style={{
            top: `${annotation.top}%`,
            left: `${annotation.left}%`,
            width: `${annotation.width}%`,
            zIndex: annotation.zIndex,
            transform: `rotate(${rotate}deg)`,
            backgroundColor: `${annotation.backgroundColor}`,
            outline: annotation.borderColor ? `${annotation.strokeWidth}px solid ${annotation.borderColor}` : "none",
            // textAlign: annotation.textAlign
            opacity: isDisplayed ? "100%" : "50%",
            overflow: "visible",
            padding: "0px",
          }}
        >
          <div
            className="annotation-label annotation-label-input"
            style={{ width: `${annotation.text === "" ? "100%" : ""}` }}
            onClick={doTextClick}
            onMouseEnter={(e: React.SyntheticEvent<HTMLDivElement>) => handleHoverIn(e)}
            onMouseLeave={(e: React.SyntheticEvent<HTMLDivElement>) => handleHoverOut(e)}
          >
            {isMenuShown && isActive ? (
              <React.Fragment>
                <RTE
                  ref={quillRef}
                  // index={index}
                  defaultValue={annotation.text}
                  onChange={handleTextChange}
                  placeholder={"Enter Text Here..."}
                  modules={{ toolbar: { container: `#menu-label-${index}` } }}
                  formats={formats}
                  onChangeSelection={handleSelectionChange}
                  preserveWhitespace
                />
              </React.Fragment>
            ) : (
              <Interweave
                content={
                  annotation.text === "" ? `${placeholderString}` : updateTextAngleBrackets(annotation.text as string)
                }
                tagName="div"
                allowAttributes
                allowElements
              />
            )}
          </div>
          <div
            className="label2Div"
            onMouseDown={handleLabel2Click}
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => handleKeyPress(e, kp)}
            onKeyUp={(e: React.KeyboardEvent<HTMLDivElement>) => handleKeyUp(e, kp)}
            tabIndex={0}
            style={{ top: "-10px", left: "-10px", position: "absolute" }}
          />
        </div>
      </React.Fragment>
    );
  };

  return <React.Fragment>{display()}</React.Fragment>;
};

export default AnnotationLabel;
